import { useState, useRef, useContext } from 'react';
import styled from 'styled-components';

import { RowContext } from 'modules/quotes/quoteItems/components/Table/Row';

import { isMobileBrowser } from 'utils/browserUtils';

const Highlight = styled.div`
  background-color: transparent;
  border: none;
  height: 100%;
  left: 0;
  outline: none;
  position: absolute;
  top: 0;
  width: 100%;
  :focus {
    background-color: rgba(33, 133, 208, 0.5);
    border: 1px solid rgb(33, 133, 208);
  }
  ${(props: { isEditing: boolean }) =>
    props.isEditing &&
    `
         border: 1px solid rgb(33, 133, 208);
       `}
`;

const StyledInput = styled.input`
  background-color: transparent;
  border: none;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  outline: none;
  text-align: right;
  width: 100%;
  padding-block: 3px;
`;

const identity = (x: unknown) => x;

type FormattedInputCellProps<T> = {
  formatter?: (val: T | string | null) => string;
  parser?: (val: string) => T;
  validator?: (val: string) => boolean;
  onBlur?: (evt: React.FocusEvent, val: T | null) => void;
  onChange?: (val: T | string | null) => void;
  column?: number;
  value?: T | null;
  defaultValue?: T | null;
};

const FormattedInput = <T,>({
  formatter = identity,
  parser = identity,
  validator = () => true,
  onBlur,
  onChange,
  column,
  value,
  defaultValue,
  ...props
}: FormattedInputCellProps<T>) => {
  const { setCellData, handleKeyDown } = useContext(RowContext);
  const inputRef = useRef<HTMLInputElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const formattedInitialValue = formatter(value ?? defaultValue ?? '');
  setCellData?.({ element: highlightRef.current }, column);
  const [isClickDisabled, setIsClickDisabled] = useState(false);

  const isEditing = document.activeElement === inputRef.current;
  const isHighlighted = document.activeElement === highlightRef.current;

  return (
    <>
      <Highlight
        ref={highlightRef}
        isEditing={isEditing}
        onClick={() => {
          if (isMobileBrowser()) {
            inputRef.current?.focus();
          } else if (isClickDisabled) {
            setIsClickDisabled(false);
          } else if (isHighlighted) {
            inputRef.current?.focus();
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            highlightRef.current?.blur();
            return;
          }

          const wasHandled = handleKeyDown?.(e, column);
          if (!wasHandled) {
            inputRef.current?.focus();
          }
        }}
        onMouseDown={() => !isHighlighted && setIsClickDisabled(true)}
        tabIndex={0}
      />
      <StyledInput
        onKeyUp={(e) => {
          if (e.key === 'Escape') {
            if (
              inputRef.current != null &&
              inputRef.current.value !== formattedInitialValue
            ) {
              inputRef.current.value = formattedInitialValue;
              onChange?.(formattedInitialValue);
            }
            highlightRef.current?.focus();
            e.preventDefault();
          }
        }}
        onBlur={(e) => {
          const containsNum = (str: string) => Boolean(str.match(/\d/));
          const localValue = e.target.value;
          const isLocalValueEmpty = localValue === '';
          const formattedValue = formatter(parser(localValue));
          const isFormattedValueValid =
            containsNum(formattedValue) && validator(formattedValue);

          const displayedValue = isLocalValueEmpty
            ? defaultValue != null
              ? formatter(defaultValue)
              : '---'
            : isFormattedValueValid
            ? formattedValue
            : formattedInitialValue;

          e.target.value = displayedValue;

          if (displayedValue !== formattedInitialValue) {
            onBlur?.(e, parser(displayedValue));
          }
        }}
        onChange={(e) => {
          onChange?.(e.target.value !== '' ? parser(e.target.value) : '');
        }}
        onFocus={(e) => {
          e.target.select();
        }}
        ref={inputRef}
        tabIndex={-1}
        defaultValue={formattedInitialValue}
        {...props}
        autoComplete="off"
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          // Land back in the nav grid on this cell first (also blurs the
          // input, triggering the save/format logic in onBlur above), then
          // let the grid's own "cell below" lookup take over focus from
          // there if one exists — reusing the exact lookup ArrowDown uses.
          highlightRef.current?.focus();
          handleKeyDown?.(
            {
              key: 'ArrowDown',
              shiftKey: false,
              preventDefault: () => undefined,
            } as unknown as React.KeyboardEvent<HTMLDivElement>,
            column,
          );
        }}
      />
    </>
  );
};

const FormattedInputCell = <T extends React.Key>(
  props: FormattedInputCellProps<T>,
) => {
  const key = props.value
    ? props.formatter?.(props.value)
    : props.defaultValue
    ? props.formatter?.(props.defaultValue)
    : '';
  return <FormattedInput key={key} {...props} />;
};

export default FormattedInputCell;
